// src/Phaser/engine/entities/Sound.ts
import Phaser from "phaser";
import type { SoundConfig } from "../../types/config";

/**
 * Représente un son (logique + intégration avec Phaser).
 * On suppose que le son a été préchargé via `this.load.audio(key, url)` avec `key === name`.
 */
export class Sound {
  private adminMode: boolean;
  public id: string;
  public name: string;   // also used as the Phaser key
  public volume: number; // 0..1
  public loop: boolean;

  private scene?: Phaser.Scene;
  private phaserSound?: Phaser.Sound.BaseSound;
  private onUnlockPlay?: () => void;

  constructor(adminMode: boolean, config: SoundConfig) {
    this.adminMode = adminMode;
    this.id = config.id;
    this.name = config.name;
    this.volume = config.volume ?? 1;
    this.loop = config.loop ?? false;
  }

  /** Crée l'instance Phaser du son (doit être appelé dans/Après preload). */
  public createPhaserSound(scene: Phaser.Scene): void {
    this.scene = scene;

    if (!scene.sound) {
      console.warn(`[Sound:${this.name}] Scene has no sound manager.`);
      return;
    }

    // Si non chargé, on avertit clairement (sinon play() plantera silencieusement)
    if (!scene.sound.get(this.name) && !scene.cache.audio.has(this.name)) {
      console.warn(`[Sound:${this.name}] Not found in cache. Did you preload it with this key?`);
    }

    this.phaserSound = scene.sound.add(this.name, { volume: this.volume, loop: this.loop });

    // Nettoyage si la scene est détruite
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.destroy());
    scene.events.once(Phaser.Scenes.Events.DESTROY, () => this.destroy());
  }

  /** Joue le son (attend l’unlock audio si nécessaire). */
  public play(): void {
    if (!this.scene) {
      console.warn(`[Sound:${this.name}] No scene attached. Call createPhaserSound() first.`);
      return;
    }
    if (!this.phaserSound) {
      console.warn(`[Sound:${this.name}] Not created. Call createPhaserSound() first.`);
      return;
    }

    const { sound, input } = this.scene;

    // Browsers may require a user gesture to unlock audio
    if (sound.locked) {
      // schedule once
      this.cancelPendingUnlock();
      this.onUnlockPlay = () => {
        this.phaserSound?.play();
        this.onUnlockPlay = undefined;
      };
      // Any first pointerdown will unlock
      input?.once(Phaser.Input.Events.POINTER_DOWN, () => {
        sound.unlock();
        this.onUnlockPlay?.();
      });
      return;
    }

    this.phaserSound.play();
  }

  /** Arrête le son s’il est en lecture. */
  public stop(): void {
    if (this.phaserSound?.isPlaying) {
      this.phaserSound.stop();
    }
  }

  /** Rejoue depuis le début (utile si déjà en cours). */
  public restart(): void {
    if (!this.phaserSound) return;
    if (this.phaserSound.isPlaying) this.phaserSound.stop();
    this.play();
  }

  /** Volume 0..1 */
  private getAnySound(): any {
    return this.phaserSound as any;
  }

  public setVolume(newVolume: number): void {
    this.volume = newVolume;
    this.getAnySound()?.setVolume(newVolume);
  }

  public setLoop(loop: boolean): void {
    this.loop = loop;
    this.getAnySound()?.setLoop(loop);
  }


  /** Fondu entrant. */
  public fadeIn(durationMs = 300): void {
    if (!this.phaserSound) return;
    const s = this.phaserSound as any;
    s.setVolume(0);
    this.play();
    this.scene?.tweens.add({
      targets: s,
      volume: this.volume,
      duration: durationMs,
      ease: "Linear",
    });
  }

  /** Fondu sortant (stop à la fin). */
  public fadeOut(durationMs = 300): void {
    if (!this.phaserSound || !this.phaserSound.isPlaying) return;
    const s = this.phaserSound;
    this.scene?.tweens.add({
      targets: s,
      volume: 0,
      duration: durationMs,
      ease: "Linear",
      onComplete: () => s.stop(),
    });
  }

  /** Libère toutes les ressources. */
  public destroy(): void {
    this.cancelPendingUnlock();
    if (this.phaserSound) {
      // stop (silence) before destroy to avoid clicks
      if (this.phaserSound.isPlaying) this.phaserSound.stop();
      this.phaserSound.destroy();
      this.phaserSound = undefined;
    }
    this.scene = undefined;
  }

  private cancelPendingUnlock() {
    this.onUnlockPlay = undefined;
  }
}
